<template>
  <section data-test="recycling-offer-screen">
    <RevStepper
      :active-step="i18n(translations.stepShippingLabel)"
      :alternative-text-back="i18n(translations.previousPage)"
      :alternative-text-close="i18n(translations.stepperClose)"
      :alternative-text-completed="i18n(translations.stepperCompleted)"
      :alternative-text-current="i18n(translations.stepperCurrent)"
      class="mb-20 sm:hidden"
      :close-button-label="i18n(translations.closeButtonLabel)"
      :steps="steps"
    />
    <div
      class="mood-cornflower flex justify-center py-12 sm:bg-static-default-low sm:py-48"
    >
      <RevContainer class="sm:max-w-[326px] sm:text-center">
        <picture>
          <RevIllustrationSource
            :height="184"
            :media="BreakpointQueries[Breakpoint.SM]"
            src="/img/recycling/recyclingOfferHero.svg"
            :width="184"
          />
          <RevIllustration
            :alt="i18n(translations.confirmationTitle)"
            class="bg-static-default-low rounded-lg m-auto"
            :height="327"
            src="/img/recycling/recyclingOfferHero.svg"
            :width="327"
          />
        </picture>
        <h1 class="heading-1 mt-8 sm:mt-24">
          {{ i18n(translations.confirmationTitle) }}
        </h1>
        <p class="body-1 mt-8">
          {{ i18n(translations.confirmationDescription) }}
        </p>
        <RevButton
          :key="i18n(translations.confirmationPrintButtonTitle) + 'desktop'"
          class="mood-main mt-8 max-sm:hidden sm:mt-24"
          full-width="always"
          :to="data?.shippingLabelUrl"
          variant="primary"
        >
          {{ i18n(translations.confirmationPrintButtonTitle) }}
        </RevButton>
        <RevButton
          :key="i18n(translations.confirmationPrintButtonTitle) + 'mobile'"
          class="mood-main mt-8 sm:mt-24 sm:hidden"
          full-width="always"
          :to="data?.shippingLabelUrl"
          variant="secondary"
        >
          {{ i18n(translations.confirmationPrintButtonTitle) }}
        </RevButton>
      </RevContainer>
    </div>
    <RevContainer class="m-auto py-12 sm:py-48">
      <h2 class="heading-2">
        {{ i18n(translations.confirmationNextStepsTitle) }}
      </h2>
      <RevTextList class="mt-24" variant="ordered">
        <template #default>
          <RevTextListItem key="0" class="flex">
            <div>
              <p class="body-1-bold">
                {{ i18n(translations.confirmationNextStepsDataTitle) }}
              </p>
              <p>
                {{ i18n(translations.confirmationNextStepsDataDescription) }}
              </p>
              <RevDrawer
                :close-button-label="i18n(translations.closeButtonLabel)"
                name="DELETE_YOUR_DATA"
                :title="i18n(translations.deleteDataNavigationTitle)"
              >
                <template #body>
                  <div class="grid">
                    <h3 class="heading-3">
                      {{ i18n(translations.deleteDataTitle) }}
                    </h3>
                    <p class="body-1 mt-16">
                      {{ i18n(translations.deleteDataDescription) }}
                    </p>
                    <RevLink
                      v-if="buybackConfig?.faq?.backup"
                      :to="buybackConfig.faq.backup"
                      >{{
                        i18n(translations.deleteDataBackupLinkTitle)
                      }}</RevLink
                    >
                    <p class="body-1 mt-16">
                      {{ i18n(translations.deleteDataSubtitle) }}
                    </p>
                    <RevLink
                      v-if="buybackConfig?.faq?.icloud"
                      :to="buybackConfig.faq.icloud"
                      >{{
                        i18n(translations.deleteDataIcloudLinkTitle)
                      }}</RevLink
                    >
                    <RevLink
                      v-if="buybackConfig?.faq?.google"
                      :to="buybackConfig.faq.google"
                      >{{
                        i18n(translations.deleteDataGoogleLinkTitle)
                      }}</RevLink
                    >
                  </div>
                </template>
                <template #trigger="{ open }">
                  <RevLink class="body-1-bold" @click="open">{{
                    i18n(translations.confirmationNextStepsDataLink)
                  }}</RevLink>
                </template>
              </RevDrawer>
            </div>
          </RevTextListItem>
          <RevTextListItem key="1" class="flex">
            <div>
              <p class="body-1-bold">
                {{ i18n(translations.confirmationNextStepsPackTitle) }}
              </p>
              <p class="whitespace-pre-wrap">
                {{ i18n(translations.confirmationNextStepsPackDescription) }}
              </p>
            </div>
          </RevTextListItem>
          <RevTextListItem key="2" class="flex">
            <div>
              <p class="body-1-bold">
                {{ i18n(translations.confirmationNextStepsShipTitle) }}
              </p>
              <FormattedMessage
                class="body-1 mt-12"
                :definition="translations.confirmationNextStepsShipDescription"
              >
                <template #dropOffLink>
                  <RevLink
                    v-if="buybackConfig?.faq?.dropOff"
                    target="_blank"
                    :to="buybackConfig.faq.dropOff"
                  >
                    {{ i18n(translations.confirmationNextStepsDropOff) }}
                  </RevLink>
                </template>
                <template #laPosteLink>
                  <RevLink
                    v-if="buybackConfig?.faq?.laPoste"
                    target="_blank"
                    :to="buybackConfig.faq.laPoste"
                  >
                    {{ i18n(translations.confirmationNextStepsLaPoste) }}
                  </RevLink>
                </template>
              </FormattedMessage>
            </div>
          </RevTextListItem>
        </template>
      </RevTextList>
      <RevButton
        class="mt-44 sm:hidden"
        full-width="always"
        to="/"
        variant="primary"
      >
        {{ i18n(translations.confirmationCloseCta) }}
      </RevButton>
      <div
        class="bg-static-info-mid rounded-lg mt-48 flex max-w-[736px] flex-col md:flex-row"
      >
        <div
          class="mx-8 my-56 flex flex-col justify-center sm:mx-48 md:justify-start"
        >
          <h3 class="heading-1 text-center md:text-left">
            {{ i18n(translations.confirmationAppsTitle) }}
          </h3>
          <p class="body-1-bold mt-8 text-center md:text-left">
            {{ i18n(translations.confirmationAppsDescription) }}
          </p>
          <AppStoreButtons
            campaign="buyback_confirmation"
            class="mt-32 flex justify-center md:justify-start"
          />
        </div>
        <div
          class="relative mt-auto flex h-[214px] w-full max-md:justify-center md:max-w-[320px]"
        >
          <RevIllustration
            alt="phone"
            class="absolute bottom-0"
            data-test="mobileAppImg"
            :height="214"
            src="/img/recycling/phoneBanner.svg"
            :width="154"
          />
        </div>
      </div>
    </RevContainer>
  </section>
</template>

<script lang="ts" setup>
import { createError, useRoute } from '#imports'

import { recyclingAPI } from '@backmarket/http-api'
import { useHttpFetch } from '@backmarket/nuxt-module-http/useHttpFetch'
import FormattedMessage from '@backmarket/nuxt-module-i18n/FormattedMessage.vue'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import {
  Breakpoint,
  BreakpointQueries,
} from '@backmarket/utils/dom/getCurrentBreakpoint'
import { RevButton } from '@ds/components/Button'
import { RevContainer } from '@ds/components/Container'
import { RevDrawer } from '@ds/components/Drawer'
import { RevIllustration } from '@ds/components/Illustration'
import { RevIllustrationSource } from '@ds/components/IllustrationSource'
import { RevLink } from '@ds/components/Link'
import { RevStepper } from '@ds/components/Stepper'
import { RevTextList } from '@ds/components/TextList'
import { RevTextListItem } from '@ds/components/TextListItem'

import { useBuybackConfig } from '~/scopes/buyback/config/useBuybackConfig'

import translations from './Recycling.translations'

const route = useRoute()
const buybackConfig = useBuybackConfig()
const i18n = useI18n()
const {
  market: { countryCode: marketCode },
} = useMarketplace()

const RECYCLING_CATEGORIES = {
  smartphone: 'SMARTPHONE',
}

const category =
  typeof route.params.deviceCategory === 'string'
    ? RECYCLING_CATEGORIES[
        route.params.deviceCategory as keyof typeof RECYCLING_CATEGORIES
      ]
    : ''

const { data, error } = await useHttpFetch(recyclingAPI.createRecycling, {
  body: {
    marketCode,
    category,
  },
})

const steps = [
  i18n(translations.categoryNavigationTitle),
  i18n(translations.stepDevice),
  i18n(translations.stepShippingLabel),
].map((item) => ({
  label: item,
  name: item,
}))

if (error.value) {
  throw createError({
    statusCode: error.value.statusCode,
    statusMessage: error.value.message,
    fatal: true,
  })
}
</script>
